<template>
  <v-container fluid class="entity-directory px-0">
    <v-dialog v-model="dialogDelete" max-width="500px">
      <v-card>
        <v-card-title class="dialog_title">
          {{ is_bulk_delete ? `Delete ${bulk_delete_items.length} Files` : "Delete Item" }}
        </v-card-title>
        <v-card-text class="dialog_content">
          <div v-if="is_bulk_delete">
            Delete {{ bulk_delete_items.length }} selected files?
            <ul class="bulk-delete-list">
              <li
                v-for="item in bulk_delete_items"
                :key="`bulk-delete-${item.id}`"
              >
                {{ item.name }}
              </li>
            </ul>
          </div>
          <div v-else>
            Are you sure you want to delete this item
            {{
              curr_delete_item && curr_delete_item.is_directory
                ? "and all its children"
                : ""
            }}?
          </div>
        </v-card-text>
        <v-card-actions>
          <v-row class="ma-0 pa-0">
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <v-btn
                class="action_text"
                outlined
                color="black"
                @click="closeDeleteDialog"
                ><span class="action_text">Cancel</span></v-btn
              >
            </v-col>
            <v-col cols="auto">
              <v-btn class="action_text" outlined color="black" @click="confirmDelete"
                ><span class="action_text">Delete</span></v-btn
              >
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogCreate" max-width="1200px" width="80vw">
      <v-card>
        <v-card-title class="dialog_title"> Create New Folder </v-card-title>
        <v-card-text class="dialog_content">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="createPayload.folder_name"
                  label="Folder Name"
                  outlined
                >
                </v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-row class="ma-0 pa-0">
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <v-btn
                class="action_text"
                outlined
                color="black"
                @click="dialogCreate = false"
                ><span class="action_text">Cancel</span></v-btn
              >
            </v-col>
            <v-col cols="auto">
              <v-btn class="action_text" outlined color="black" @click="confirmCreate"
                ><span class="action_text">Add</span></v-btn
              >
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogEdit" max-width="1200px" width="80vw">
      <v-card>
        <v-card-title class="dialog_title"> Edit Item Name </v-card-title>
        <v-card-text class="dialog_content">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field v-model="editPayload.name" label="New Name" outlined>
                </v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-row class="ma-0 pa-0">
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <v-btn
                class="action_text"
                outlined
                color="black"
                @click="dialogEdit = false"
                ><span class="action_text">Cancel</span></v-btn
              >
            </v-col>
            <v-col cols="auto">
              <v-btn class="action_text" outlined color="black" @click="confirmEdit"
                ><span class="action_text">Save Changes</span></v-btn
              >
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogMove"
      content-class="directory-move-dialog"
      max-width="1200px"
      width="80vw"
    >
      <v-card>
        <v-card-title class="dialog_title"> Move Files </v-card-title>
        <v-card-text class="dialog_content">
          <div class="directory-move-dialog__summary">
            Move {{ selected_files.length }} selected files to:
          </div>
          <v-treeview
            class="treeview_directory directory-move-dialog__tree"
            :active.sync="move_target_item"
            :items="move_directory_tree_data"
            :open.sync="moveOpenedNodes"
            transition
            expand-icon=""
          >
            <template v-slot:prepend="{ item, open }">
              <v-icon
                v-if="hasMoveDirectoryChildren(item)"
                class="directory-move-dialog__toggle"
                color="black"
                @click.stop="toggleMoveNode(item)"
              >
                {{ open ? "mdi-menu-down" : "mdi-menu-right" }}
              </v-icon>
            </template>
            <template v-slot:label="{ item }">
              <span
                :class="[
                  'directory-move-dialog__label',
                  {
                    'directory-move-dialog__label--active': isMoveTargetSelected(item.id),
                    'directory-move-dialog__label--disabled': isCurrentActiveFolder(item.id),
                  },
                ]"
                @click.stop="selectMoveTarget(item)"
              >
                {{ item.name }}
              </span>
            </template>
          </v-treeview>
        </v-card-text>
        <v-card-actions>
          <v-row class="ma-0 pa-0">
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <v-btn
                class="action_text"
                outlined
                color="black"
                @click="closeMoveDialog"
                ><span class="action_text">Cancel</span></v-btn
              >
            </v-col>
            <v-col cols="auto">
              <v-btn
                class="action_text"
                outlined
                color="black"
                @click="confirmMove"
                :disabled="is_move_confirm_disabled"
                ><span class="action_text">Move</span></v-btn
              >
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card flat class="directory-card mx-0">
      <v-row
        v-if="!directory_data || directory_data.length == 0"
        align="center"
        justify="center"
        class="directory-empty-state"
      >
        <v-col
          cols="auto"
          class="directory-empty-state__content"
        >
          <div class="text-h6 grey--text text--lighten-1 font-weight-light">
            Create a new folder to get started
          </div>
          <v-btn
            v-if="can_create_folder"
            icon
            @click="openRootCreateDialog"
            :disabled="loading"
          >
            <v-icon color="black">mdi-folder-plus-outline</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-else class="directory-content-row ma-0 pa-0">
        <v-col
          cols="3"
          class="ma-0 pl-0 py-0"
          :class="{ 'directory-tree-pane--loading': loading }"
          style="height: 100%"
        >
          <v-row
            class="directory-tree-header ma-0 pa-0"
            align="center"
            justify="center"
          >
            <v-col cols="auto" class="pl-0 pt-0">
              <span class="section_title">Explorer</span>
            </v-col>
            <v-spacer></v-spacer>
            <v-col
              cols="auto"
              class="directory-tree-actions pr-0 pt-0"
            >
              <template v-if="can_create_folder">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="directory-tree-action"
                      icon
                      elevation="0"
                      v-on="on"
                      @click="openRootCreateDialog"
                      :disabled="loading"
                    >
                      <v-icon color="black">mdi-folder-plus-outline</v-icon>
                    </v-btn>
                  </template>
                  <TooltipDescription title="Create Root Folder"></TooltipDescription>
                </v-tooltip>
              </template>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    class="directory-tree-action"
                    icon
                    elevation="0"
                    v-on="on"
                    @click="reset_directory"
                    :disabled="loading"
                  >
                    <v-icon color="black">mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <TooltipDescription title="Refresh Directory"></TooltipDescription>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row class="directory-tree-body ma-0 pa-0">
            <v-treeview
              class="treeview_directory"
              :active.sync="active_item"
              :items="directory_tree_data"
              activatable
              open-on-click
              transition
              expand-icon=""
              :open.sync="openedNodes"
              @update:active="on_folder_active"
              @update:open="on_folder_expand"
              :disabled="loading"
            >
              <template v-slot:prepend="{ item, open }">
                <v-icon
                  v-if="
                    item.children &&
                    item.children.filter((item) => item.is_directory).length > 0
                  "
                  color="black"
                >
                  {{ open ? "mdi-menu-down" : "mdi-menu-right" }}
                </v-icon>
              </template>
            </v-treeview>
          </v-row>
        </v-col>

        <v-divider vertical light class="directory-divider"></v-divider>

        <v-col class="text-center py-0 directory-detail-pane" cols="9">
          <div v-if="loading" class="directory_loading_state">
            <div class="directory_loading_state__content">
              <v-progress-circular
                color="black"
                size="72"
                indeterminate
              ></v-progress-circular>
            </div>
          </div>
          <div v-else class="directory-detail-content">
            <div
              v-if="!active_folder"
              class="directory-empty-selection text-h6 grey--text text--lighten-1 font-weight-light"
            >
              Select an {{ entity_name }}
            </div>
            <div v-else>
              <div class="directory-section-row">
                <div class="directory-section-main">
                  <v-card-title class="folder_name directory-folder-title pt-0">
                    <div
                      v-if="is_inline_editing_active_folder"
                      class="directory-folder-title__editor"
                    >
                      <v-text-field
                        ref="folderNameInput"
                        v-model="editPayload.name"
                        class="directory-folder-title__input"
                        dense
                        hide-details
                        single-line
                        @keydown.enter.prevent="confirmEdit"
                        @keydown.esc.prevent="cancelInlineEdit"
                      ></v-text-field>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            class="directory-folder-title__rename"
                            icon
                            v-on="on"
                            @click="confirmEdit"
                            :disabled="!editPayload.name || !editPayload.name.trim()"
                          >
                            <v-icon color="black">mdi-check</v-icon>
                          </v-btn>
                        </template>
                        <TooltipDescription title="Save Name"></TooltipDescription>
                      </v-tooltip>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            class="directory-folder-title__rename"
                            icon
                            v-on="on"
                            @click="cancelInlineEdit"
                          >
                            <v-icon color="black">mdi-close</v-icon>
                          </v-btn>
                        </template>
                        <TooltipDescription title="Cancel Rename"></TooltipDescription>
                      </v-tooltip>
                    </div>
                    <template v-else>
                      <span class="directory-folder-title__text">
                        {{ active_folder.name }}
                      </span>
                      <v-tooltip v-if="active_folder && !disable_folder_actions" top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            class="directory-folder-title__rename"
                            icon
                            v-on="on"
                            @click="openInlineFolderEdit(active_folder)"
                          >
                            <v-icon color="black">mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <TooltipDescription title="Rename Folder"></TooltipDescription>
                      </v-tooltip>
                    </template>
                  </v-card-title>
                  <v-card-subtitle class="folder_details directory-subtitle py-1">
                    {{
                      active_folder.children.filter((item) => item.is_directory).length
                    }}
                    Folder,
                    {{
                      active_folder.children.filter((item) => !item.is_directory).length
                    }}
                    {{ entity_name.toLowerCase() }}s
                  </v-card-subtitle>
                </div>
                <div v-if="active_folder && !disable_folder_actions">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-if="can_create_folder"
                        icon
                        v-on="on"
                        @click="createSelected('create_folder')"
                      >
                        <v-icon color="black">mdi-folder-plus-outline</v-icon>
                      </v-btn>
                    </template>
                    <TooltipDescription title="Create New Folder"></TooltipDescription>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-if="can_create_file"
                        icon
                        v-on="on"
                        @click="createSelected('create_file')"
                      >
                        <v-icon color="black">mdi-file-plus</v-icon>
                      </v-btn>
                    </template>
                    <TooltipDescription title="Create New File"></TooltipDescription>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn icon v-on="on" @click="openDeleteDialog(active_folder)">
                        <v-icon color="black">mdi-folder-remove-outline</v-icon>
                      </v-btn>
                    </template>
                    <TooltipDescription title="Delete Folder"></TooltipDescription>
                  </v-tooltip>
                </div>
              </div>
              <div class="directory-section-row">
                <div class="directory-section-main">
                  <v-card-title class="black--text section_title directory-section-title">
                    Folders
                  </v-card-title>
                  <div v-if="active_folder.children.filter((item) => item.is_directory).length == 0" class="folder_container">
                    <v-card-subtitle class="empty_entity_text directory-subtitle">
                      No subfolders
                    </v-card-subtitle>
                  </div>
                  <div v-else class="folder_container">
                    <span
                      v-for="item in active_folder.children.filter(
                        (item) => item.is_directory
                      )"
                      :key="item.id"
                      class="folder_item"
                    >
                      <v-btn
                        icon
                        elevation="0"
                        @click="on_folder_open(item.id)"
                      >
                        <v-icon x-large color="black">mdi-folder</v-icon>
                      </v-btn>
                      <div class="folder_item_label">
                        {{ item.name }}
                      </div>
                    </span>
                  </div>
                </div>
              </div>

              <div class="directory-section-row">
                <div class="directory-section-main">
                  <v-card-title class="black--text section_title directory-section-title pb-0">
                    <div class="directory-section-title-label">Files</div>
                  </v-card-title>
                  <div class="directory-file-list-header" v-if="active_folder.children.filter((item) => !item.is_directory).length > 0">
                    <div class="directory-file-list-header__select">
                      <v-checkbox
                        class="directory-select-all"
                        dense
                        hide-details
                        :disabled="loading || delete_eligible_files.length === 0"
                        :indeterminate="is_file_selection_indeterminate"
                        :input-value="are_all_delete_eligible_files_selected"
                        @change="toggleSelectAllFiles"
                      ></v-checkbox>
                      <span class="directory-file-list-header__label">
                        Select all
                      </span>
                      <span
                        v-if="selected_files.length > 0"
                        class="directory-selection-controls__count"
                      >
                        ({{ selected_files.length }} selected)
                      </span>
                    </div>
                    <div class="directory-file-list-header__actions">
                      <v-select
                        v-model="file_sort_key"
                        class="directory-sort-select"
                        :items="file_sort_options"
                        dense
                        hide-details
                        item-text="text"
                        item-value="value"
                        label="Sort by"
                        outlined
                      ></v-select>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            class="directory-sort-direction"
                            icon
                            small
                            v-on="on"
                            @click="toggleFileSortDirection"
                          >
                            <v-icon color="black">
                              {{ file_sort_direction === "asc" ? "mdi-sort-ascending" : "mdi-sort-descending" }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <TooltipDescription :title="file_sort_direction === 'asc' ? 'Ascending' : 'Descending'"></TooltipDescription>
                      </v-tooltip>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            class="directory-selection-controls__button"
                            icon
                            small
                            v-on="on"
                            @click="openBulkMoveDialog"
                            :disabled="loading || selected_files.length === 0"
                          >
                            <v-icon color="black">mdi-folder-move-outline</v-icon>
                          </v-btn>
                        </template>
                        <TooltipDescription title="Move selected"></TooltipDescription>
                      </v-tooltip>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            class="directory-selection-controls__button"
                            icon
                            small
                            v-on="on"
                            @click="openBulkDeleteDialog"
                            :disabled="loading || selected_files.length === 0"
                          >
                            <v-icon color="red">mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </template>
                        <TooltipDescription title="Delete selected"></TooltipDescription>
                      </v-tooltip>
                    </div>
                  </div>
                  <v-card-subtitle
                    v-if="
                      active_folder.children.filter((item) => !item.is_directory)
                        .length == 0
                    "
                    class="empty_entity_text directory-subtitle pt-4"
                  >
                    No {{ entity_name.toLowerCase() }} in this folder
                  </v-card-subtitle>
                  <div v-else class="file_container pl-4">
                    <v-card
                      v-for="item in sorted_active_files"
                      :key="item.id"
                      rounded
                      outlined
                      width="100%"
                      :class="[
                        'rounded-lg',
                        'card_wrapper',
                        { 'card_wrapper--selected': isFileSelected(item) },
                      ]"
                    >
                      <v-row class="mx-0" align="center">
                        <v-col cols="auto" class="px-1">
                          <v-checkbox
                            class="directory-file-checkbox"
                            dense
                            hide-details
                            :disabled="loading || !canDeleteFile(item)"
                            :input-value="isFileSelected(item)"
                            @change="toggleFileSelection(item, $event)"
                          ></v-checkbox>
                        </v-col>
                        <v-col cols="6">
                          <v-list-item-content class="card_content_wrapper">
                            <v-list-item-title class="card_title card_content">{{
                              item.name
                            }}</v-list-item-title>
                            <template
                              v-for="subcontent in resolved_item_subcontent_config"
                            >
                              <v-list-item-subtitle
                                v-if="item[subcontent.key]"
                                :key="`${item.id}-${subcontent.key}`"
                                class="item_subtitle card_content"
                              >
                                {{ subcontent.label }}: {{ item[subcontent.key] }}
                              </v-list-item-subtitle>
                            </template>
                          </v-list-item-content>
                        </v-col>
                        <v-spacer></v-spacer>
                        <v-col cols="auto" class="px-1">
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                icon
                                elevation="0"
                                v-on="on"
                                @click="openEditDialog(item)"
                                :disabled="loading || (item.status != 'PROCESSED' && item.status != 'ACTIVE')"
                              >
                                <v-icon color="black">mdi-rename-outline</v-icon>
                              </v-btn>
                            </template>
                            <span class="tooltip_text">Rename</span>
                          </v-tooltip>
                        </v-col>
                        <v-col cols="auto" class="px-1">
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                icon
                                elevation="0"
                                v-on="on"
                                @click="$emit('open_entity', item.id)"
                                :disabled="loading || (item.status != 'PROCESSED' && item.status != 'PENDING' && item.status != 'ACTIVE')"
                              >
                                <v-icon color="black">mdi-open-in-app</v-icon>
                              </v-btn>
                            </template>
                            <span class="tooltip_text">Open</span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-card>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import TooltipDescription from "@/components/Utility/TooltipDescription.vue";
import { DELAY } from "@/constants/constants.js";

export default {
  name: "EntityDirectory",
  props: [
    "entity_name",
    "directory_data",
    "available_create_options",
    "item_subcontent_config",
    "loading",
    "auto_select_first_folder",
    "disable_folder_actions",
  ],
  components: {
    TooltipDescription,
  },

  data: () => ({
    dialogDelete: false,
    dialogCreate: false,
    dialogEdit: false,
    dialogMove: false,
    curr_delete_item: null,
    is_bulk_delete: false,
    bulk_delete_items: [],
    editPayload: {},
    createPayload: {},
    active_item: [],
    active_folder: null,
    inline_editing_folder_id: null,
    selected_file_ids: [],
    move_target_item: [],
    moveOpenedNodes: [],
    openedNodes: [],
    oldOpenedNodes: [],
    file_sort_key: "updated_time",
    file_sort_direction: "asc",
    file_sort_options: [
      { text: "Name", value: "name" },
      { text: "Last Update", value: "updated_time" },
    ],
  }),

  computed: {
    can_create_folder() {
      return (
        !this.available_create_options ||
        this.available_create_options.includes("create_folder")
      );
    },
    can_create_file() {
      return (
        !this.available_create_options ||
        this.available_create_options.includes("create_file")
      );
    },
    is_inline_editing_active_folder() {
      return (
        this.active_folder &&
        this.inline_editing_folder_id !== null &&
        String(this.inline_editing_folder_id) === String(this.active_folder.id)
      );
    },
    directory_tree_data() {
      return this.filterDirectoryTreeItems(this.directory_data);
    },
    move_directory_tree_data() {
      return this.addMoveTargetDisabledState(this.directory_tree_data);
    },
    active_files() {
      if (!this.active_folder || !Array.isArray(this.active_folder.children)) {
        return [];
      }

      return this.active_folder.children.filter((item) => !item.is_directory);
    },
    sorted_active_files() {
      const directionMultiplier = this.file_sort_direction === "desc" ? -1 : 1;

      return [...this.active_files].sort((itemA, itemB) => {
        const comparison = this.compareFileSortValues(itemA, itemB, this.file_sort_key);
        if (comparison !== 0) {
          return comparison * directionMultiplier;
        }

        return this.compareFileSortValues(itemA, itemB, "name");
      });
    },
    delete_eligible_files() {
      return this.active_files.filter((item) => this.canDeleteFile(item));
    },
    selected_files() {
      const selectedIds = this.selected_file_ids.map((id) => String(id));

      return this.active_files.filter((item) => selectedIds.includes(String(item.id)));
    },
    are_all_delete_eligible_files_selected() {
      if (this.delete_eligible_files.length === 0) {
        return false;
      }

      return this.delete_eligible_files.every((item) => this.isFileSelected(item));
    },
    is_file_selection_indeterminate() {
      return (
        this.selected_files.length > 0 &&
        !this.are_all_delete_eligible_files_selected
      );
    },
    is_move_confirm_disabled() {
      if (this.loading || this.move_target_item.length === 0) {
        return true;
      }

      return this.isCurrentActiveFolder(this.move_target_item[0]);
    },
    resolved_item_subcontent_config() {
      const defaultConfig = [
        { key: "status", label: "Status" },
        { key: "updated_time", label: "Last Update" },
      ];

      const config = this.item_subcontent_config
        ? this.item_subcontent_config
        : defaultConfig;

      return config
        .map((entry) => {
          if (typeof entry === "string") {
            return {
              key: entry,
              label: this.formatSubcontentLabel(entry),
            };
          }

          const key = entry.key || entry.value;

          if (!key) {
            return null;
          }

          return {
            key,
            label: entry.label || entry.text || this.formatSubcontentLabel(key),
          };
        })
        .filter(Boolean);
    },
  },

  watch: {
    directory_data: {
      immediate: true,
      handler() {
        this.clearFileSelection();
        this.syncActiveFolder();
      },
    },
  },

  methods: {
    syncActiveFolder() {
      const activeFolderId =
        this.active_folder &&
        this.active_folder.id !== null &&
        this.active_folder.id !== undefined
          ? this.active_folder.id
          : this.active_item[0];

      if (activeFolderId === null || activeFolderId === undefined) {
        if (this.auto_select_first_folder) {
          const firstFolder = this.findFirstFolder(this.directory_data);

          if (firstFolder) {
            this.active_folder = firstFolder;
            this.active_item = [firstFolder.id];
          }
        }

        return;
      }

      const activeFolder = this.findTreeItem(this.directory_data, activeFolderId);

      if (!activeFolder) {
        this.active_folder = null;
        this.active_item = [];
        this.openedNodes = [];
        this.oldOpenedNodes = [];
        return;
      }

      this.active_folder = activeFolder;
      this.active_item = [activeFolder.id];
      this.openedNodes = this.openedNodes.filter((id) =>
        this.findTreeItem(this.directory_data, id)
      );
      this.oldOpenedNodes = this.oldOpenedNodes.filter((id) =>
        this.findTreeItem(this.directory_data, id)
      );
    },
    formatSubcontentLabel(key) {
      return key
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    filterDirectoryTreeItems: function (items) {
      if (!Array.isArray(items)) {
        return [];
      }

      return items.filter((item) => item.is_directory).map((item) => {
        const directoryChildren = this.filterDirectoryTreeItems(item.children);
        const treeItem = { ...item };

        if (directoryChildren.length > 0) {
          treeItem.children = directoryChildren;
        } else {
          delete treeItem.children;
        }

        return treeItem;
      });
    },
    addMoveTargetDisabledState: function (items) {
      if (!Array.isArray(items)) {
        return [];
      }

      return items.map((item) => {
        const treeItem = {
          ...item,
          disabled: this.isCurrentActiveFolder(item.id),
        };
        const children = this.addMoveTargetDisabledState(item.children);

        if (children.length > 0) {
          treeItem.children = children;
        } else {
          delete treeItem.children;
        }

        return treeItem;
      });
    },
    isCurrentActiveFolder: function (folderId) {
      return (
        this.active_folder &&
        String(this.active_folder.id) === String(folderId)
      );
    },
    hasMoveDirectoryChildren: function (item) {
      return (
        item &&
        Array.isArray(item.children) &&
        item.children.some((child) => child.is_directory)
      );
    },
    isMoveTargetSelected: function (folderId) {
      return (
        this.move_target_item.length > 0 &&
        String(this.move_target_item[0]) === String(folderId)
      );
    },
    selectMoveTarget: function (item) {
      if (!item || this.isCurrentActiveFolder(item.id)) {
        this.move_target_item = [];
        return;
      }

      this.move_target_item = [item.id];
    },
    toggleMoveNode: function (item) {
      if (!this.hasMoveDirectoryChildren(item)) {
        return;
      }

      const itemId = String(item.id);
      const openedIds = this.moveOpenedNodes.map((id) => String(id));

      if (openedIds.includes(itemId)) {
        this.moveOpenedNodes = this.moveOpenedNodes.filter(
          (id) => String(id) !== itemId
        );
        return;
      }

      this.moveOpenedNodes = [...this.moveOpenedNodes, item.id];
    },
    on_folder_active(newActiveItems) {
      if (this.loading || !newActiveItems || newActiveItems.length === 0) {
        return;
      }

      const active_folder = this.findTreeItem(this.directory_data, newActiveItems[0]);

      if (!active_folder) {
        return;
      }

      this.active_folder = active_folder;
      this.clearFileSelection();
    },
    on_folder_expand(newOpenedNodes) {
      if (this.loading) {
        this.openedNodes = [...this.oldOpenedNodes];
        return;
      }

      let activeId = null;

      if (newOpenedNodes.length > this.oldOpenedNodes.length) {
        const expandedId = newOpenedNodes.find((id) => !this.oldOpenedNodes.includes(id));
        activeId = expandedId;
      }
      else if (newOpenedNodes.length < this.oldOpenedNodes.length) {
        const collapsedId = this.oldOpenedNodes.find(
          (id) => !newOpenedNodes.includes(id)
        );
        activeId = collapsedId;
      }

      if (activeId) {
        const active_folder = this.findTreeItem(this.directory_data, activeId);
        if (!active_folder) {
          this.oldOpenedNodes = [...newOpenedNodes];
          return;
        }

        this.active_folder = active_folder;
        this.active_item = [active_folder.id];
        this.clearFileSelection();
      }

      this.oldOpenedNodes = [...newOpenedNodes];
    },
    on_folder_open: async function (folderId) {
      if (this.loading) {
        return;
      }

      let parentIds = this.findNestedParentIds(this.directory_data, folderId);

      this.openedNodes = [...new Set([...this.openedNodes, ...parentIds])];

      await DELAY(50);
      const active_folder = this.findTreeItem(this.directory_data, folderId);
      this.active_folder = active_folder;
      this.active_item = [folderId];
      this.oldOpenedNodes = [...this.openedNodes];
      this.clearFileSelection();
    },
    findNestedParentIds: function (items, id, parents = []) {
      if (!items) {
        return null;
      }
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (item.id == id) {
          return parents;
        }
        const result = this.findNestedParentIds(item.children, id, [...parents, item.id]);
        if (result) {
          return result;
        }
      }
      return null;
    },
    findTreeItem: function (items, id) {
      if (!items) {
        return;
      }
      for (var i = 0; i < items.length; i++) {
        var item = items[i];

        if (item.id == id) {
          return item;
        }
        const child = this.findTreeItem(item.children, id);
        if (child) {
          return child;
        }
      }
    },
    findFirstFolder: function (items) {
      if (!items) {
        return;
      }

      for (var i = 0; i < items.length; i++) {
        var item = items[i];

        if (item.is_directory) {
          return item;
        }

        const child = this.findFirstFolder(item.children);
        if (child) {
          return child;
        }
      }
    },

    openRootCreateDialog: function () {
      this.openCreateDialog(null);
    },

    createSelected: function (id) {
      if (this.loading) {
        return;
      }

      if (id == "create_folder") {
        this.openCreateDialog(this.active_folder);
      } else if (id == "create_file") {
        this.$emit("create_entity", this.active_folder.id);
      }
    },

    reset_directory: function () {
      if (this.loading) {
        return;
      }

      this.clearFileSelection();
      this.$emit("reset_directory");
    },

    openDeleteDialog: function (item) {
      if (this.loading) {
        return;
      }

      this.curr_delete_item = item;
      this.is_bulk_delete = false;
      this.bulk_delete_items = [];
      this.dialogDelete = true;
    },

    closeDeleteDialog: function () {
      this.dialogDelete = false;
      this.curr_delete_item = null;
      this.is_bulk_delete = false;
      this.bulk_delete_items = [];
      this.clearFileSelection();
    },

    canDeleteFile: function (item) {
      return (
        item &&
        !item.is_directory &&
        (item.status == "PROCESSED" || item.status == "FAILED" || item.status == "ACTIVE")
      );
    },

    isFileSelected: function (item) {
      return (
        item &&
        this.selected_file_ids
          .map((id) => String(id))
          .includes(String(item.id))
      );
    },

    toggleFileSelection: function (item, selected) {
      if (!this.canDeleteFile(item)) {
        return;
      }

      const itemId = String(item.id);
      const selectedIds = this.selected_file_ids.map((id) => String(id));

      if (selected && !selectedIds.includes(itemId)) {
        this.selected_file_ids = [...this.selected_file_ids, item.id];
      } else if (!selected) {
        this.selected_file_ids = this.selected_file_ids.filter(
          (id) => String(id) !== itemId
        );
      }
    },

    toggleSelectAllFiles: function (selected) {
      if (selected) {
        this.selected_file_ids = this.delete_eligible_files.map((item) => item.id);
        return;
      }

      this.clearFileSelection();
    },

    clearFileSelection: function () {
      this.selected_file_ids = [];
    },
    toggleFileSortDirection: function () {
      this.file_sort_direction = this.file_sort_direction === "asc" ? "desc" : "asc";
    },
    compareFileSortValues: function (itemA, itemB, sortKey) {
      if (sortKey === "updated_time") {
        const dateA = this.getFileUpdatedTimestamp(itemA);
        const dateB = this.getFileUpdatedTimestamp(itemB);

        if (dateA !== dateB) {
          return dateA - dateB;
        }
      }

      const nameA = String(itemA && itemA.name ? itemA.name : "").toLowerCase();
      const nameB = String(itemB && itemB.name ? itemB.name : "").toLowerCase();
      return nameA.localeCompare(nameB, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    },
    getFileUpdatedTimestamp: function (item) {
      const rawDate =
        item &&
        (item.updated_time ||
          item.updated_at ||
          item.last_updated ||
          item.last_update_date);

      if (!rawDate) {
        return 0;
      }

      const timestamp = new Date(rawDate).getTime();
      return Number.isFinite(timestamp) ? timestamp : 0;
    },

    openBulkDeleteDialog: function () {
      if (this.loading || this.selected_files.length === 0) {
        return;
      }

      this.curr_delete_item = null;
      this.is_bulk_delete = true;
      this.bulk_delete_items = [...this.selected_files];
      this.dialogDelete = true;
    },

    openBulkMoveDialog: function () {
      if (
        this.loading ||
        this.selected_files.length === 0
      ) {
        return;
      }

      this.move_target_item = [];
      this.moveOpenedNodes = [...this.openedNodes];
      this.dialogMove = true;
    },

    on_move_target_active: function (newActiveItems) {
      if (
        !newActiveItems ||
        newActiveItems.length === 0 ||
        this.isCurrentActiveFolder(newActiveItems[0])
      ) {
        this.move_target_item = [];
      }
    },

    closeMoveDialog: function () {
      this.dialogMove = false;
      this.move_target_item = [];
      this.moveOpenedNodes = [];
    },

    confirmMove: function () {
      if (this.is_move_confirm_disabled || this.selected_file_ids.length === 0) {
        return;
      }

      this.$emit("move_files", {
        entity_ids: [...this.selected_file_ids],
        parent_id: this.move_target_item[0],
      });
      this.closeMoveDialog();
      this.clearFileSelection();
    },

    confirmDelete: function () {
      if (this.loading) {
        return;
      }

      if (this.is_bulk_delete) {
        this.bulk_delete_items.forEach((item) => {
          this.$emit("delete_entity", item.id);
        });
        this.dialogDelete = false;
        this.is_bulk_delete = false;
        this.bulk_delete_items = [];
        this.curr_delete_item = null;
        this.clearFileSelection();
        return;
      }

      if (this.curr_delete_item) {
        this.$emit("delete_entity", this.curr_delete_item.id);
        this.dialogDelete = false;
        this.curr_delete_item = null;
      }
    },

    openCreateDialog: function (item) {
      if (this.loading) {
        return;
      }

      this.createPayload = {
        folder_name: "",
        parent_id: item ? item.id : -1,
      };
      this.dialogCreate = true;
    },

    confirmCreate: function () {
      if (this.loading) {
        return;
      }

      if (this.createPayload.folder_name.trim()) {
        this.$emit("create_entity_folder", this.createPayload);
        this.dialogCreate = false;
        this.createPayload = {
          folder_name: "",
          parent_id: null,
        };
      }
    },

    openEditDialog: function (item) {
      if (this.loading) {
        return;
      }

      this.editPayload = {
        id: item.id,
        name: item.name,
      };
      this.dialogEdit = true;
    },

    openInlineFolderEdit: function (item) {
      if (this.loading) {
        return;
      }

      this.editPayload = {
        id: item.id,
        name: item.name,
      };
      this.inline_editing_folder_id = item.id;
      this.dialogEdit = false;
      this.$nextTick(() => {
        const input = this.$refs.folderNameInput;
        if (input && input.focus) {
          input.focus();
        }
      });
    },

    cancelInlineEdit: function () {
      this.inline_editing_folder_id = null;
      this.editPayload = {
        id: null,
        name: "",
      };
    },

    confirmEdit: function () {
      if (this.loading) {
        return;
      }

      if (this.editPayload.name.trim()) {
        this.$emit("edit_entity_name", this.editPayload);
        this.dialogEdit = false;
        this.inline_editing_folder_id = null;
        this.editPayload = {
          id: null,
          name: "",
        };
      }
    },
  },
};
</script>
<style>
.entity-directory,
.directory-card {
  height: 100%;
}

.directory-empty-state {
  height: 100%;
}

.directory-empty-state__content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.directory-content-row {
  height: 100%;
  overflow-y: auto;
}

.directory-tree-header {
  max-height: 10%;
  width: 100%;
}

.directory-tree-actions {
  display: flex;
  align-items: center;
  gap: 0;
}

.directory-tree-action.v-btn--icon.v-size--default {
  height: 36px;
  width: 36px;
}

.directory-tree-body {
  height: 90%;
  width: 100%;
}

.directory-tree-body .v-treeview-node__root {
  margin-left: -24px;
  padding-left: 0;
}

.treeview_directory {
  width: 100%;
}

.directory-divider {
  align-self: stretch;
  height: auto;
  min-height: 100%;
  border-color: black !important;
}

.dialog_content {
  font-family: "Poppins" !important;
  font-weight: 400;
  font-size: 1em;
}
.bulk-delete-list {
  margin-top: 10px;
  max-height: 180px;
  overflow-y: auto;
  padding-left: 20px;
}
.dialog_title {
  font-family: "Poppins" !important;
  font-weight: 600 !important;
  font-size: 1.2em !important;
}
.action_text,
.action_text .v-btn__content {
  font-family: "Poppins" !important;
  font-weight: 400 !important;
  font-size: 0.9em !important;
}
.folder_name {
  font-family: "Poppins" !important;
  font-weight: 600;
  font-size: 2em;
  line-height: 1.2;
}
.directory-folder-title {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 40px;
}
.directory-folder-title__text {
  display: inline-block;
  line-height: 1.2;
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  padding: 0;
  text-align: left;
}
.directory-folder-title__editor {
  align-items: flex-start;
  display: flex;
  flex: 0 1 auto;
  gap: 6px;
  height: 40px;
  line-height: 1.2;
  min-width: 0;
  overflow: visible;
}
.directory-folder-title__input {
  align-items: flex-start;
  flex: 0 1 460px;
  height: 40px;
  line-height: normal;
  max-width: min(460px, calc(100vw - 220px));
  min-width: 180px;
  overflow: visible;
  width: 460px;
}
.directory-folder-title__input.v-input {
  display: block;
  font-size: 1rem;
  height: 40px;
  line-height: normal;
  margin: 0;
  overflow: visible;
  padding: 0;
}
.directory-folder-title__input .v-input__control,
.directory-folder-title__input .v-input__slot {
  height: auto !important;
  min-height: 40px !important;
}
.directory-folder-title__input .v-input__slot {
  align-items: flex-start;
  margin: 0;
  padding: 0;
}
.directory-folder-title__input .v-input__slot::before,
.directory-folder-title__input .v-input__slot::after {
  display: none;
}
.directory-folder-title__input .v-text-field__slot {
  align-items: flex-start;
  height: auto;
  line-height: normal;
  min-height: 40px;
}
.directory-folder-title__input input {
  background: transparent;
  border: 0;
  color: inherit;
  font-family: "Poppins" !important;
  font-size: 2rem;
  font-weight: 600;
  height: auto;
  line-height: normal;
  max-height: none;
  min-height: 40px;
  outline: 0;
  overflow: visible;
  padding: 0 !important;
  transform: translateY(-4px);
}
.directory-folder-title__rename.v-btn--icon.v-size--default {
  flex: 0 0 auto;
  height: 36px;
  width: 36px;
}
.folder_details {
  font-family: "Poppins" !important;
  font-weight: 400;
  font-size: 1em;
}
.folder_item {
  flex: 0 0 20% !important;
  max-width: 20% !important;
}
.folder_item_label {
  font-family: "Poppins" !important;
  font-size: 0.8em;
}
.card_wrapper {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 16px;
}
.card_wrapper--selected {
  background: #f5f5f5;
  border-color: #222 !important;
}
.card_content_wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 16px;
}
.card_content {
  flex-grow: 1;
  text-align: left;
}
.section_title {
  font-family: "Poppins" !important;
  font-weight: 600;
  font-size: 1.1em;
  flex-grow: 1;
  text-align: left;
}
.card_title {
  font-family: "Poppins" !important;
  font-weight: 600;
  font-size: 1em;
}
.item_subtitle {
  font-family: "Poppins" !important;
}
.empty_entity_text {
  font-family: "Poppins" !important;
  font-weight: 400 !important;
  font-size: 0.9em !important;
}
.directory-empty-selection {
  align-self: center;
  font-family: "Poppins" !important;
}
.directory-section-row {
  display: flex;
}
.directory-section-main {
  flex: 3;
}
.directory-section-title {
  display: flex;
  align-items: center;
}
.directory-section-title-label {
  flex: 3;
}
.directory-file-list-header {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 4px;
  min-height: 48px;

  padding: 8px 20px;
}
.directory-file-list-header__select,
.directory-file-list-header__actions {
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
}
.directory-file-list-header__label {
  font-family: "Poppins" !important;
  font-size: 0.85em;
  font-weight: 600;
  white-space: nowrap;
}
.directory-selection-controls__count {
  font-family: "Poppins" !important;
  font-size: 0.85em;
  font-weight: 600;
  white-space: nowrap;
}
.directory-selection-controls__button.v-btn--icon.v-size--small {
  height: 32px;
  width: 32px;
}
.directory-select-all {
  flex: 0 0 auto;
  margin-top: 0;
  padding-top: 0;
}
.directory-file-checkbox {
  margin-top: 0;
  padding-top: 0;
}
.directory-move-dialog {
  width: 80vw;
  max-width: 900px;
}
@media (min-width: 760px) {
  .directory-move-dialog {
    min-width: 720px;
  }
}
.directory-move-dialog__toggle {
  cursor: pointer;
}
.directory-move-dialog__label {
  cursor: pointer;
  display: inline-block;
  font-family: "Poppins" !important;
  padding: 2px 4px;
}
.directory-move-dialog__label--active {
  font-weight: 600;
}
.directory-move-dialog__label--disabled {
  color: #9e9e9e;
  cursor: default;
}
.directory-subtitle {
  float: left;
}
.directory-detail-pane {
  display: flex;
  flex-direction: column;
  max-height: 100%;
}
.directory-detail-content {
  flex: 1;
}
.directory_loading_state {
  flex: 1;
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.directory_loading_state__content {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
}
.directory-tree-pane--loading {
  opacity: 0.55;
  pointer-events: none;
}

div.folder_container {
  display: flex;
  overflow-x: visible;
  overflow-y: hidden;
  white-space: nowrap;
  gap: 10px;
  width: 100%;
  height: 80px;
}

span.folder_item {
  flex: 0 0 auto;
  width: 80px;
}

div.file_container {
  max-height: calc(45vh - 56px);
  overflow-y: scroll;
}
</style>
